<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useCeoDashboard } from 'dashboard/composables/useCeoDashboard';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

// Layout / header
import ReportHeader from './components/ReportHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Tab containers
import CommandCenterTab from './components/ceo/tabs/CommandCenterTab.vue';
import AiPerformanceTab from './components/ceo/tabs/AiPerformanceTab.vue';
import RevenueImpactTab from './components/ceo/tabs/RevenueImpactTab.vue';
import OperationsTab from './components/ceo/tabs/OperationsTab.vue';
import ChargebacksTab from './components/ceo/tabs/ChargebacksTab.vue';

const { t } = useI18n();

const {
  PERIODS,
  selectedPeriod,
  dateRange,
  isLoading,
  ceoDashboardData,
  reportsSummary,
  previousReportsSummary,
  fetchAll,
  setSelectedPeriod,
} = useCeoDashboard();

const [showDropdown, toggleDropdown] = useToggle();

const activeTabIndex = ref(0);

const tabs = computed(() => [
  { label: t('CEO_DASHBOARD.TABS.COMMAND_CENTER') },
  { label: t('CEO_DASHBOARD.TABS.AI_PERFORMANCE') },
  { label: t('CEO_DASHBOARD.TABS.REVENUE_IMPACT') },
  { label: t('CEO_DASHBOARD.TABS.OPERATIONS') },
  { label: t('CEO_DASHBOARD.TABS.CHARGEBACKS') },
]);

const periodMenuList = computed(() =>
  Object.values(PERIODS).map(period => ({
    label: t(`CEO_DASHBOARD.PERIOD.${period.key}`),
    value: period.key,
    action: 'select',
  }))
);

const selectedPeriodLabel = computed(() =>
  t(`CEO_DASHBOARD.PERIOD.${selectedPeriod.value.key}`)
);

const handlePeriodChange = ({ value }) => {
  toggleDropdown(false);
  setSelectedPeriod(PERIODS[value]);
  fetchAll();
};

const handleTabChanged = tab => {
  activeTabIndex.value = tabs.value.findIndex(t => t.label === tab.label);
};

const { startRefetching } = useLiveRefresh(fetchAll, 120000);

onMounted(() => {
  fetchAll();
  startRefetching();
});
</script>

<template>
  <div class="flex items-center justify-between px-1 py-2">
    <ReportHeader :header-title="t('CEO_DASHBOARD.HEADER')" />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group z-50"
    >
      <Button
        sm
        slate
        faded
        :label="selectedPeriodLabel"
        class="capitalize rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showDropdown"
        :menu-items="periodMenuList"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handlePeriodChange($event)"
      />
    </div>
  </div>

  <div class="flex flex-col gap-4 pb-6">
    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      @tab-changed="handleTabChanged"
    />

    <CommandCenterTab
      v-if="activeTabIndex === 0"
      :ceo-dashboard-data="ceoDashboardData"
      :reports-summary="reportsSummary"
      :previous-reports-summary="previousReportsSummary"
      :date-range="dateRange"
      :is-loading="isLoading"
    />

    <AiPerformanceTab
      v-else-if="activeTabIndex === 1"
      :ceo-dashboard-data="ceoDashboardData"
      :date-range="dateRange"
      :is-loading="isLoading"
    />

    <RevenueImpactTab
      v-else-if="activeTabIndex === 2"
      :ceo-dashboard-data="ceoDashboardData"
      :is-loading="isLoading"
    />

    <OperationsTab
      v-else-if="activeTabIndex === 3"
      :ceo-dashboard-data="ceoDashboardData"
      :reports-summary="reportsSummary"
      :previous-reports-summary="previousReportsSummary"
      :date-range="dateRange"
      :is-loading="isLoading"
    />

    <ChargebacksTab
      v-else-if="activeTabIndex === 4"
      :is-loading="isLoading"
    />
  </div>
</template>
